import { gql, useQuery } from '@apollo/client';

/**
 * Editable copy for the MFA screens.
 *
 * Source of truth at runtime: CMS → Content Manager → **LoginPage** → the **mfa**
 * component (backend component `login.mfa-texts`). The strings below are the
 * defaults: they render exactly what the screens showed before this was made
 * editable, and are used as a fallback whenever the CMS value is missing/empty
 * (or before the backend field is deployed). CMS values override them field by
 * field.
 *
 * This query is intentionally self-contained (not part of the shared
 * people-graphql query) and tolerant (`errorPolicy: 'all'`) so the MFA screens
 * keep working — and the login flow is never affected — even if the `mfa`
 * component has not been deployed to the backend yet.
 */
export interface MfaTexts {
  verifyTitle: string;
  verifyDescription: string;
  verifyCodeLabel: string;
  verifyCta: string;
  verifyErrorFallback: string;
  setupTitle: string;
  setupDescription: string;
  setupPreparing: string;
  setupManualKeyLabel: string;
  setupCodeLabel: string;
  setupCta: string;
  setupErrorFallback: string;
  setupExpired: string;
  verifyExpired: string;
  backupTitle: string;
  backupDescription: string;
  backupContinueCta: string;
  codePlaceholder: string;
  loadingLabel: string;
  backToSignInCta: string;
  // Self-service reset (emailed code) flow
  resetLinkCta: string;
  resetRequestTitle: string;
  resetRequestDescription: string;
  resetEmailLabel: string;
  resetEmailPlaceholder: string;
  resetRequestCta: string;
  resetSentToast: string;
  resetCodeTitle: string;
  resetCodeDescription: string;
  resetCodeLabel: string;
  resetCodeCta: string;
  resetErrorFallback: string;
}

export const MFA_TEXT_DEFAULTS: MfaTexts = {
  verifyTitle: 'Two-step verification',
  verifyDescription: 'Enter the 6-digit code from your authenticator app. You can also use one of your backup codes.',
  verifyCodeLabel: 'Verification code',
  verifyCta: 'Verify',
  verifyErrorFallback: 'Invalid code. Please try again.',
  setupTitle: 'Set up two-step verification',
  setupDescription:
    'Your account requires an authenticator app (Google Authenticator, Authy, 1Password…). Scan the QR code, then enter the 6-digit code to finish.',
  setupPreparing: 'Preparing your setup…',
  setupManualKeyLabel: "Can't scan? Enter this key manually:",
  setupCodeLabel: 'Enter the 6-digit code',
  setupCta: 'Verify & continue',
  setupErrorFallback: 'Could not start MFA setup. Please sign in again.',
  setupExpired: 'Your setup session has expired. Please sign in again to restart setting up two-step verification.',
  verifyExpired: 'Your sign-in session has expired. Please sign in again.',
  backupTitle: 'Save your backup codes',
  backupDescription:
    "Store these somewhere safe. Each code can be used once if you lose access to your authenticator. They won't be shown again.",
  backupContinueCta: "I've saved my codes — continue",
  codePlaceholder: '123456',
  loadingLabel: 'Verifying…',
  backToSignInCta: 'Back to sign in',
  resetLinkCta: 'Lost access to your authenticator?',
  resetRequestTitle: 'Reset two-step verification',
  resetRequestDescription:
    "Enter your account email and we'll send you a code to reset two-step verification. You'll set it up again afterwards.",
  resetEmailLabel: 'Email address',
  resetEmailPlaceholder: 'you@example.com',
  resetRequestCta: 'Send reset code',
  resetSentToast: 'If the email matches your account, we’ve sent a reset code.',
  resetCodeTitle: 'Enter your reset code',
  resetCodeDescription: "Enter the 6-digit code we emailed you. Next you'll set up two-step verification again.",
  resetCodeLabel: 'Reset code',
  resetCodeCta: 'Continue',
  resetErrorFallback: 'Invalid or expired code. Please try again.',
};

const QUERY_MFA_TEXTS = gql`
  query mfaTexts {
    loginPage {
      data {
        attributes {
          mfa {
            verifyTitle
            verifyDescription
            verifyCodeLabel
            verifyCta
            verifyErrorFallback
            setupTitle
            setupDescription
            setupPreparing
            setupManualKeyLabel
            setupCodeLabel
            setupCta
            setupErrorFallback
            setupExpired
            verifyExpired
            backupTitle
            backupDescription
            backupContinueCta
            codePlaceholder
            loadingLabel
            backToSignInCta
            resetLinkCta
            resetRequestTitle
            resetRequestDescription
            resetEmailLabel
            resetEmailPlaceholder
            resetRequestCta
            resetSentToast
            resetCodeTitle
            resetCodeDescription
            resetCodeLabel
            resetCodeCta
            resetErrorFallback
          }
        }
      }
    }
  }
`;

interface MfaTextsQueryResult {
  loginPage?: {
    data?: { attributes?: { mfa?: Partial<Record<keyof MfaTexts, string | null>> | null } | null } | null;
  } | null;
}

/**
 * Returns the MFA copy: CMS values where present, defaults everywhere else.
 * Never suspends or throws — falls back to defaults on any error.
 */
export function useMfaTexts(): MfaTexts {
  const { data } = useQuery<MfaTextsQueryResult>(QUERY_MFA_TEXTS, {
    errorPolicy: 'all',
    fetchPolicy: 'cache-first',
    context: { skipAuth: true },
  });

  const cms = data?.loginPage?.data?.attributes?.mfa;
  if (!cms) return MFA_TEXT_DEFAULTS;

  // Merge, letting a non-empty CMS string win over the default for each field.
  const merged = { ...MFA_TEXT_DEFAULTS };
  (Object.keys(MFA_TEXT_DEFAULTS) as (keyof MfaTexts)[]).forEach((key) => {
    const value = cms[key];
    if (typeof value === 'string' && value.trim() !== '') {
      merged[key] = value;
    }
  });
  return merged;
}
